import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { RefreshCw, Ticket, Upload, Loader2 } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'

interface FanTier {
  id: string
  name: string
  level: number
  earlyAccessDays: number
  windowHours: number
}

interface FanTierRules {
  tiers: FanTier[]
  defaultWindowHours: number
}

interface ShowOption {
  id: string
  title: string
  date: string
  venue: string
  city: string
  generalSaleAt: string | null
}

interface TierWindow {
  tierId: string
  tierName: string
  level: number
  earlyAccessDays: number
  opensAt: string
  closesAt: string | null
}

interface SchedulePreview {
  showId: string
  generalSaleAt: string
  windows: TierWindow[]
  hiveCellsShowId: string | null
  hiveCellsReleases: Array<{ id: string; tierLevel: number; releaseAt: string; status: string }>
}

interface TicketSectionSummary {
  id: string
  showId: string
  name: string
  capacity: number
  sold: number
  held: number
}

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function TicketingMarketingSection() {
  const qc = useQueryClient()
  const [selectedShowId, setSelectedShowId] = useState('')
  const [generalSaleAt, setGeneralSaleAt] = useState('')
  const [tierDraft, setTierDraft] = useState<FanTier[]>([])
  const [syncing, setSyncing] = useState(false)
  const [savingTiers, setSavingTiers] = useState(false)
  const [pushing, setPushing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const hiveQ = useQuery<{ connected: boolean }>({
    queryKey: ['hive-cells-status'],
    queryFn: () => artistFetch<{ connected: boolean }>('/integrations/hive-cells'),
  })

  const tiersQ = useQuery<FanTierRules, ApiClientError>({
    queryKey: ['fan-tiers'],
    queryFn: () => artistFetch<FanTierRules>('/ticketing/fan-tiers'),
  })

  const showsQ = useQuery<ShowOption[], ApiClientError>({
    queryKey: ['ticketing-shows-marketing'],
    queryFn: () => artistFetch<ShowOption[]>('/ticketing/shows'),
  })

  const sectionsQ = useQuery<TicketSectionSummary[], ApiClientError>({
    queryKey: ['ticketing-sections-summary'],
    queryFn: () => artistFetch<TicketSectionSummary[]>('/ticketing/sections'),
  })

  const ticketTotals = (sectionsQ.data ?? []).reduce(
    (acc, row) => ({
      capacity: acc.capacity + row.capacity,
      sold: acc.sold + row.sold,
      held: acc.held + row.held,
    }),
    { capacity: 0, sold: 0, held: 0 },
  )

  const scheduleQ = useQuery<SchedulePreview, ApiClientError>({
    queryKey: ['tier-schedule', selectedShowId, generalSaleAt],
    queryFn: () => {
      const qs = generalSaleAt
        ? `?generalSaleAt=${encodeURIComponent(new Date(generalSaleAt).toISOString())}`
        : ''
      return artistFetch<SchedulePreview>(`/ticketing/shows/${selectedShowId}/tier-schedule${qs}`)
    },
    enabled: !!selectedShowId,
  })

  useEffect(() => {
    if (tiersQ.data?.tiers) setTierDraft(tiersQ.data.tiers)
  }, [tiersQ.data])

  useEffect(() => {
    const show = showsQ.data?.find((s) => s.id === selectedShowId)
    if (!show) return
    const sale = show.generalSaleAt ?? show.date
    setGeneralSaleAt(toDatetimeLocalValue(sale))
  }, [selectedShowId, showsQ.data])

  async function syncTiers() {
    setError(null)
    setSuccess(null)
    setSyncing(true)
    try {
      await artistFetch('/ticketing/fan-tiers/sync-hive-cells', { method: 'POST' })
      await qc.invalidateQueries({ queryKey: ['fan-tiers'] })
      setSuccess('Membership tiers synced from HiveCells.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Sync failed')
    } finally {
      setSyncing(false)
    }
  }

  async function saveTiers() {
    setError(null)
    setSuccess(null)
    setSavingTiers(true)
    try {
      await artistFetch('/ticketing/fan-tiers', {
        method: 'PATCH',
        body: {
          tiers: tierDraft,
          defaultWindowHours: tiersQ.data?.defaultWindowHours ?? 48,
        },
      })
      await qc.invalidateQueries({ queryKey: ['fan-tiers'] })
      if (selectedShowId) {
        await qc.invalidateQueries({ queryKey: ['tier-schedule', selectedShowId] })
      }
      setSuccess('Tier pre-release offsets saved.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Save failed')
    } finally {
      setSavingTiers(false)
    }
  }

  async function saveGeneralSale() {
    if (!selectedShowId || !generalSaleAt) return
    setError(null)
    setSuccess(null)
    try {
      await artistFetch(`/ticketing/shows/${selectedShowId}/general-sale`, {
        method: 'PATCH',
        body: { generalSaleAt: new Date(generalSaleAt).toISOString() },
      })
      await qc.invalidateQueries({ queryKey: ['ticketing-shows-marketing'] })
      await qc.invalidateQueries({ queryKey: ['tier-schedule', selectedShowId] })
      setSuccess('General on-sale date saved.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save on-sale date')
    }
  }

  async function pushSchedule() {
    if (!selectedShowId) return
    setError(null)
    setSuccess(null)
    setPushing(true)
    try {
      if (generalSaleAt) await saveGeneralSale()
      await artistFetch(`/ticketing/shows/${selectedShowId}/tier-schedule/push`, {
        method: 'POST',
        body: generalSaleAt
          ? { generalSaleAt: new Date(generalSaleAt).toISOString() }
          : {},
      })
      await qc.invalidateQueries({ queryKey: ['tier-schedule', selectedShowId] })
      setSuccess('Pre-release schedule pushed to HiveCells.')
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Push to HiveCells failed')
    } finally {
      setPushing(false)
    }
  }

  const connected = hiveQ.data?.connected === true

  return (
    <div className="space-y-6">
      <PerformanceScopeBanner />

      <div className="rounded-lg border border-ovation-ink/10 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg">Ticket sales</h2>
            <p className="text-sm text-ovation-muted">
              Live sold, held, and capacity across all ticket sections.
            </p>
          </div>
          <Link
            to="/ticketing"
            className="inline-flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Ticket className="h-4 w-4" />
            Full ticketing view
          </Link>
        </div>
        <dl className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div className="rounded-md bg-ovation-ink/5 px-3 py-3">
            <dt className="text-xs uppercase tracking-wider text-ovation-muted">Sold</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">{ticketTotals.sold.toLocaleString()}</dd>
          </div>
          <div className="rounded-md bg-ovation-ink/5 px-3 py-3">
            <dt className="text-xs uppercase tracking-wider text-ovation-muted">Held</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">{ticketTotals.held.toLocaleString()}</dd>
          </div>
          <div className="rounded-md bg-ovation-ink/5 px-3 py-3">
            <dt className="text-xs uppercase tracking-wider text-ovation-muted">Capacity</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">{ticketTotals.capacity.toLocaleString()}</dd>
          </div>
        </dl>
      </div>

      <div className="rounded-lg border border-ovation-ink/10 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg">HiveCells membership tiers</h2>
            <p className="text-sm text-ovation-muted">
              Pull tier definitions from your Hive Cell, then set how many days before general
              on-sale each tier can buy (T−X).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!connected && (
              <Link
                to="/integrations"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 hover:bg-amber-100"
              >
                Connect HiveCells
              </Link>
            )}
            <button
              type="button"
              disabled={!connected || syncing}
              onClick={() => void syncTiers()}
              className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync tiers
            </button>
          </div>
        </div>

        <AsyncBoundary isLoading={tiersQ.isLoading} error={tiersQ.error}>
          {tierDraft.length === 0 ? (
            <p className="text-sm text-ovation-muted">
              No tiers yet. Connect HiveCells and click Sync tiers to import your membership levels.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ovation-ink/10 text-left text-xs uppercase tracking-wider text-ovation-muted">
                    <th className="py-2 pr-4">Tier</th>
                    <th className="py-2 pr-4">Level</th>
                    <th className="py-2 pr-4">Pre-release (T− days)</th>
                    <th className="py-2">Window (hours)</th>
                  </tr>
                </thead>
                <tbody>
                  {tierDraft.map((tier, index) => (
                    <tr key={tier.id} className="border-b border-ovation-ink/5">
                      <td className="py-2 pr-4 font-medium">{tier.name}</td>
                      <td className="py-2 pr-4 text-ovation-muted">{tier.level}</td>
                      <td className="py-2 pr-4">
                        <input
                          type="number"
                          min={0}
                          value={tier.earlyAccessDays}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10)
                            setTierDraft((prev) =>
                              prev.map((t, i) =>
                                i === index
                                  ? { ...t, earlyAccessDays: Number.isNaN(v) ? 0 : v }
                                  : t,
                              ),
                            )
                          }}
                          className="w-24 rounded-md border border-ovation-ink/15 px-2 py-1"
                        />
                      </td>
                      <td className="py-2">
                        <input
                          type="number"
                          min={0}
                          value={tier.windowHours}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10)
                            setTierDraft((prev) =>
                              prev.map((t, i) =>
                                i === index
                                  ? { ...t, windowHours: Number.isNaN(v) ? 0 : v }
                                  : t,
                              ),
                            )
                          }}
                          className="w-24 rounded-md border border-ovation-ink/15 px-2 py-1"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                disabled={savingTiers}
                onClick={() => void saveTiers()}
                className="mt-4 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
              >
                {savingTiers ? 'Saving…' : 'Save tier offsets'}
              </button>
            </div>
          )}
        </AsyncBoundary>
      </div>

      <div className="rounded-lg border border-ovation-ink/10 bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5 text-ovation-muted" />
          <h2 className="font-display text-lg">Pre-release schedule</h2>
        </div>

        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
              Show
            </label>
            <select
              value={selectedShowId}
              onChange={(e) => setSelectedShowId(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            >
              <option value="">— pick a show —</option>
              {(showsQ.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {new Date(s.date).toLocaleDateString()} — {s.venue}, {s.city}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
              General on-sale (T)
            </label>
            <input
              type="datetime-local"
              value={generalSaleAt}
              onChange={(e) => setGeneralSaleAt(e.target.value)}
              disabled={!selectedShowId}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm disabled:opacity-50"
            />
          </div>
        </div>

        {selectedShowId && (
          <AsyncBoundary isLoading={scheduleQ.isLoading} error={scheduleQ.error}>
            {scheduleQ.data && (
              <>
                {!scheduleQ.data.hiveCellsShowId && (
                  <p className="mb-3 text-sm text-amber-800">
                    This show is not in HiveCells yet — confirm it in the pipeline so ticket
                    sections sync, then push the pre-release schedule.
                  </p>
                )}
                {scheduleQ.data.windows.length > 0 ? (
                  <div className="mb-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-ovation-ink/10 text-left text-xs uppercase tracking-wider text-ovation-muted">
                          <th className="py-2 pr-4">Tier</th>
                          <th className="py-2 pr-4">Opens (T−)</th>
                          <th className="py-2 pr-4">Closes</th>
                          <th className="py-2">HiveCells</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scheduleQ.data.windows.map((w) => {
                          const remote = scheduleQ.data!.hiveCellsReleases.find(
                            (r) => r.tierLevel === w.level,
                          )
                          return (
                            <tr key={w.tierId} className="border-b border-ovation-ink/5">
                              <td className="py-2 pr-4 font-medium">{w.tierName}</td>
                              <td className="py-2 pr-4">
                                <span className="text-ovation-muted">T−{w.earlyAccessDays}</span>
                                <div>{new Date(w.opensAt).toLocaleString()}</div>
                              </td>
                              <td className="py-2 pr-4">
                                {w.closesAt ? new Date(w.closesAt).toLocaleString() : '—'}
                              </td>
                              <td className="py-2">
                                {remote ? (
                                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                                    {remote.status}
                                  </span>
                                ) : (
                                  <span className="text-xs text-ovation-muted">Not pushed</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mb-4 text-sm text-ovation-muted">
                    Sync membership tiers above to preview the pre-release calendar.
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={!generalSaleAt}
                    onClick={() => void saveGeneralSale()}
                    className="rounded-md border border-ovation-ink/15 px-4 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
                  >
                    Save on-sale date
                  </button>
                  <button
                    type="button"
                    disabled={pushing || !connected || tierDraft.length === 0}
                    onClick={() => void pushSchedule()}
                    className="inline-flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
                  >
                    {pushing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Push schedule to HiveCells
                  </button>
                </div>
              </>
            )}
          </AsyncBoundary>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}
    </div>
  )
}
